// ChapterAdminTab.jsx — Admin Panel "Chapter" sub-tab, extracted from
// LimitedPartnerPortal. Chapter list + tabbed edit form (Identity/Content/
// Photos/Visibility), hero + gallery photo uploads, legacy-chapter seeding,
// and the New Chapter wizard. Form/editing state stays lifted in the portal
// so drafts survive sub-tab switches; uploadingPhotoFor is shared with the
// Users and Grant awards sub-tabs. normalizeFoundingWindow is exported for
// the Super Admin Tools anniversary-badge backfill.
import React from "react";
import { doc, setDoc, updateDoc, deleteDoc, Timestamp } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL, deleteObject } from "firebase/storage";
import { db, storage } from "../../../../firebaseConfig.js";
import { RetroButton, AdminTabStrip } from "../../../ui/retro";
import StatusIcon from "../../../icons/StatusIcon";
import NewChapterWizard from "../../chapters/NewChapterWizard";
import { slugifyChapter } from "../../../../helpers/slugifyChapter";

// --- Chapters management handlers (superAdmin only) ---

// Founding LP badge window choices, in days from the chapter's founded
// date. Stored on the chapter doc as `foundingWindowDays` and honored by every
// award path (inviteUser auto-award, the anniversary backfill below, and the
// badge definitions). Chapters without the field default to the first year.
const FOUNDING_WINDOW_OPTIONS = [
  { days: 90, label: 'First quarter (90 days)' },
  { days: 180, label: 'First two quarters (180 days)' },
  { days: 270, label: 'First three quarters (270 days)' },
  { days: 365, label: 'First year (365 days)' },
];
const DEFAULT_FOUNDING_WINDOW_DAYS = 365;
export const normalizeFoundingWindow = (value) => {
  const n = Number(value);
  return FOUNDING_WINDOW_OPTIONS.some((o) => o.days === n) ? n : DEFAULT_FOUNDING_WINDOW_DAYS;
};

// Blank form template, shared by reset paths.
const emptyChapterForm = {
  slug: '', name: '', pageSlug: '', tagline: '', foundedYear: '', foundedDate: '',
  foundingWindowDays: DEFAULT_FOUNDING_WINDOW_DAYS,
  emailAlias: '', slackChannel: '', lpSlackChannel: '', active: true, order: 0,
  heroTitle: '', heroTagline: '', heroImage: '', heroImageCaption: '',
  servingTitle: '', servingText: '',
  counties: [], poweredByText: '', galleryPhotos: [],
  showLPs: true, showGallery: true, showImpact: true,
  // Grant Awardees grid is opt-in per chapter (off by default) — it's a
  // heavier section that only makes sense once a chapter has picked winners.
  showAwardees: false,
  // Dollar value used by the Statistics tab when a winner doc has no explicit
  // awardAmount. Empty/null cascades to HISTORICAL_DEFAULT_GRANT in awardAmount.js.
  defaultGrantAmount: ''
};

// Normalizes a counties value from the form (array OR newline/comma-separated
// string from a textarea) into a clean array of trimmed, deduped strings.
const normalizeCounties = (value) => {
  const list = Array.isArray(value)
    ? value
    : String(value || '').split(/[\n,]/);
  const cleaned = list.map(s => String(s).trim()).filter(Boolean);
  return [...new Set(cleaned)];
};

export default function ChapterAdminTab({
  user,
  users,
  chapters,
  isSuperAdmin,
  isChapterDirector,
  userChapter,
  editingChapterId,
  setEditingChapterId,
  chapterFormData,
  setChapterFormData,
  chapterFormTab,
  setChapterFormTab,
  isAddingChapter,
  setIsAddingChapter,
  showNewChapterWizard,
  setShowNewChapterWizard,
  uploadingPhotoFor,
  setUploadingPhotoFor,
  loadChapters,
  requestConfirm,
  showAppAlert,
  showAppConfirm,
}) {
// ───── Chapter hero + gallery photo uploads ─────
// Hero path:    chapter-photos/{chapterSlug}/hero      (stable, no extension)
// Gallery path: chapter-photos/{chapterSlug}/gallery-{ts}.{ext}
//
// Hero uploads use a STABLE path so the static chapter HTML can hardcode the
// download URL — visitors get the latest upload on first paint with no
// Firestore round-trip and no flash. Each new upload overwrites the same
// object; Cache-Control: max-age=300 means the change is visible to visitors
// within 5 min. URL is the canonical token-less Firebase Storage REST URL,
// which works because storage.rules grants public read on chapter-photos/*.
//
// Gallery photos remain timestamped — they're a list, so each upload is a
// new URL. The 24h cache there is fine because URLs rotate.
//
// Writes to storage, then persists the URL back to the /chapters/{slug} doc
// immediately so the form doesn't need a separate Save click to retain the
// upload. Storage rules allow this for superAdmin or the chapter's director.
const uploadChapterPhoto = async (chapterSlug, file, kind) => {
  const ext = (file.name.split('.').pop() || 'jpg').toLowerCase().replace(/[^a-z0-9]/g, '') || 'jpg';
  const path = kind === 'hero'
    ? `chapter-photos/${chapterSlug}/hero`
    : `chapter-photos/${chapterSlug}/${kind}-${Date.now()}.${ext}`;
  const storageRef = ref(storage, path);
  const metadata = {
    contentType: file.type,
    cacheControl: kind === 'hero'
      ? 'public, max-age=300, must-revalidate'
      : 'public, max-age=86400',
  };
  await uploadBytes(storageRef, file, metadata);
  const url = kind === 'hero'
    // Canonical token-less URL — stable across uploads, governed by storage.rules.
    // Bucket is hardcoded (matches firebaseConfig.js) to avoid runtime SDK
    // version dependence on storage.app.options.storageBucket.
    ? `https://firebasestorage.googleapis.com/v0/b/gnf-app-9d7e3.firebasestorage.app/o/${encodeURIComponent(path)}?alt=media`
    // Token-bearing URL — needed for timestamped paths since each upload is a
    // new object and a stable URL would 404 once the file is replaced.
    : await getDownloadURL(storageRef);
  return { url, storagePath: path };
};

const handleChapterHeroUpload = async (file) => {
  if (!file || !editingChapterId) return;
  if (!isSuperAdmin && !isChapterDirector) {
    showAppAlert("Only chapter directors and super admins can upload chapter photos.");
    return;
  }
  if (!file.type.startsWith('image/')) {
    showAppAlert("Please choose an image file (PNG, JPG, etc.).");
    return;
  }
  if (file.size > 5 * 1024 * 1024) {
    showAppAlert("Image must be smaller than 5 MB.");
    return;
  }
  setUploadingPhotoFor(prev => ({ ...prev, [`hero:${editingChapterId}`]: true }));
  try {
    const { url, storagePath } = await uploadChapterPhoto(editingChapterId, file, 'hero');
    // If there was a previous hero image we uploaded, best-effort delete it to
    // avoid stranding orphans in storage. We only try when the previous URL
    // includes our own path prefix so we don't try to delete external URLs.
    const prevPath = chapterFormData.heroImageStoragePath;
    await updateDoc(doc(db, "chapters", editingChapterId), {
      heroImage: url,
      heroImageStoragePath: storagePath,
      updatedAt: Timestamp.now(),
    });
    setChapterFormData(prev => ({ ...prev, heroImage: url, heroImageStoragePath: storagePath }));
    if (prevPath && prevPath !== storagePath) {
      try { await deleteObject(ref(storage, prevPath)); } catch (_) { /* best-effort */ }
    }
    loadChapters();
  } catch (err) {
    console.error("LPPortal: chapter hero upload failed:", err);
    showAppAlert(err.code === 'storage/unauthorized'
      ? "Upload failed: you don't have permission on this chapter."
      : `Upload failed: ${err.message || err}`);
  } finally {
    setUploadingPhotoFor(prev => { const n = { ...prev }; delete n[`hero:${editingChapterId}`]; return n; });
  }
};

const handleGalleryPhotoUpload = async (file) => {
  if (!file || !editingChapterId) return;
  if (!isSuperAdmin && !isChapterDirector) {
    showAppAlert("Only chapter directors and super admins can upload chapter photos.");
    return;
  }
  if (!file.type.startsWith('image/')) {
    showAppAlert("Please choose an image file.");
    return;
  }
  if (file.size > 5 * 1024 * 1024) {
    showAppAlert("Image must be smaller than 5 MB.");
    return;
  }
  setUploadingPhotoFor(prev => ({ ...prev, [`gallery:${editingChapterId}`]: true }));
  try {
    const { url, storagePath } = await uploadChapterPhoto(editingChapterId, file, 'gallery');
    const current = Array.isArray(chapterFormData.galleryPhotos) ? chapterFormData.galleryPhotos : [];
    const next = [...current, { url, storagePath }];
    await updateDoc(doc(db, "chapters", editingChapterId), {
      galleryPhotos: next,
      updatedAt: Timestamp.now(),
    });
    setChapterFormData(prev => ({ ...prev, galleryPhotos: next }));
    loadChapters();
  } catch (err) {
    console.error("LPPortal: chapter gallery upload failed:", err);
    showAppAlert(err.code === 'storage/unauthorized'
      ? "Upload failed: you don't have permission on this chapter."
      : `Upload failed: ${err.message || err}`);
  } finally {
    setUploadingPhotoFor(prev => { const n = { ...prev }; delete n[`gallery:${editingChapterId}`]; return n; });
  }
};

const handleGalleryPhotoDelete = async (index) => {
  if (!editingChapterId) return;
  if (!isSuperAdmin && !isChapterDirector) return;
  const current = Array.isArray(chapterFormData.galleryPhotos) ? chapterFormData.galleryPhotos : [];
  const target = current[index];
  if (!target) return;
  const ok = await requestConfirm({
    title: 'Remove this photo from the gallery?',
    confirmLabel: 'Remove',
    variant: 'danger',
    message: <p>The file will also be deleted from storage.</p>,
  });
  if (!ok) return;
  const next = current.filter((_, i) => i !== index);
  try {
    await updateDoc(doc(db, "chapters", editingChapterId), {
      galleryPhotos: next,
      updatedAt: Timestamp.now(),
    });
    setChapterFormData(prev => ({ ...prev, galleryPhotos: next }));
    if (target.storagePath) {
      try { await deleteObject(ref(storage, target.storagePath)); } catch (_) { /* best-effort */ }
    }
    loadChapters();
  } catch (err) {
    console.error("LPPortal: chapter gallery delete failed:", err);
    showAppAlert(`Delete failed: ${err.message || err}`);
  }
};

const handleSaveChapter = async () => {
  if (!isSuperAdmin && !isChapterDirector) {
    showAppAlert("Only Super Admins and Chapter Directors can edit chapters.");
    return;
  }
  const f = chapterFormData;
  // On edit, the target doc ID must not change — use editingChapterId verbatim.
  // On create, normalize the user's slug input (or derive from the name).
  const slug = editingChapterId ? editingChapterId : slugifyChapter(f.slug || f.name);
  if (!f.name || !slug) {
    showAppAlert("Chapter name and slug are required.");
    return;
  }
  // Chapter directors are scoped to their own chapter for edits, and cannot
  // create new chapters at all. Enforced in firestore.rules too.
  if (isChapterDirector && !isSuperAdmin) {
    if (isAddingChapter) {
      showAppAlert("Only Super Admins can create new chapters.");
      return;
    }
    const target = chapters.find(c => c.id === editingChapterId);
    if (!target || target.name !== userChapter) {
      showAppAlert(`Chapter directors can only edit their own chapter (${userChapter}).`);
      return;
    }
  }
  // Uniqueness only matters on create.
  if (isAddingChapter && chapters.some(c => c.id === slug)) {
    showAppAlert(`A chapter with slug "${slug}" already exists.`);
    return;
  }
  try {
    const docRef = doc(db, "chapters", slug);
    // Content fields any director is allowed to write (must match the
    // directorEditableFields allowlist in firestore.rules).
    const contentPayload = {
      heroTitle: (f.heroTitle || '').trim(),
      heroTagline: (f.heroTagline || '').trim(),
      heroImageCaption: (f.heroImageCaption || '').trim(),
      servingTitle: (f.servingTitle || '').trim(),
      servingText: (f.servingText || '').trim(),
      counties: normalizeCounties(f.counties),
      poweredByText: (f.poweredByText || '').trim(),
      showLPs: f.showLPs !== false,
      showGallery: f.showGallery !== false,
      // Defaults to true (matching ChapterPage's destructured default and the
      // static HTML where the impact section starts visible) — so only an
      // explicit toggle to false hides it.
      showImpact: f.showImpact !== false,
      // Strict === true so absence/null defaults to hidden, matching the
      // ChapterPage default and the static-HTML section's initial display:none.
      showAwardees: f.showAwardees === true,
      // Per-chapter default grant size for the Statistics tab fallback chain.
      // null on blank input so the resolver cascades to HISTORICAL_DEFAULT_GRANT.
      defaultGrantAmount: f.defaultGrantAmount === '' || f.defaultGrantAmount == null
        ? null
        : (Number.isFinite(Number(f.defaultGrantAmount)) && Number(f.defaultGrantAmount) > 0
            ? Math.round(Number(f.defaultGrantAmount))
            : null),
      updatedAt: Timestamp.now()
    };
    // heroImage and galleryPhotos are saved immediately by the upload handlers
    // (handleChapterHeroUpload / handleGalleryPhotoUpload) so we do NOT include
    // them in the Save payload — otherwise a stale form snapshot would clobber
    // a photo that was uploaded seconds earlier.
    let payload;
    if (isSuperAdmin) {
      // SuperAdmins write the full doc (metadata + content).
      payload = {
        name: f.name.trim(),
        slug,
        // pageSlug defaults to slug when blank.
        pageSlug: ((f.pageSlug || '').trim() || slug),
        tagline: (f.tagline || '').trim(),
        foundedYear: f.foundedYear === '' || f.foundedYear == null
          ? null
          : (Number.isFinite(Number(f.foundedYear)) ? Number(f.foundedYear) : null),
        // Full founding date drives Founding LP badge eligibility (1-year window).
        // foundedYear stays as a coarse fallback for legacy chapters that haven't set this.
        foundedDate: (() => {
          const v = (f.foundedDate || '').trim();
          if (!v) return null;
          const parsed = new Date(v);
          return isNaN(parsed.getTime()) ? null : Timestamp.fromDate(parsed);
        })(),
        foundingWindowDays: normalizeFoundingWindow(f.foundingWindowDays),
        emailAlias: (f.emailAlias || '').trim(),
        slackChannel: (f.slackChannel || '').trim(),
        lpSlackChannel: (f.lpSlackChannel || '').trim(),
        active: f.active !== false,
        order: Number.isFinite(Number(f.order)) ? Number(f.order) : 0,
        ...contentPayload
      };
      if (isAddingChapter) {
        payload.createdAt = Timestamp.now();
        payload.createdBy = user.uid;
      }
    } else {
      // Chapter directors can only touch the content allowlist. Firestore
      // rules also enforce this — we just keep the client write minimal to
      // avoid tripping the affectedKeys().hasOnly(...) check.
      payload = contentPayload;
    }
    await setDoc(docRef, payload, { merge: true });
    showAppAlert(`Chapter "${f.name.trim()}" saved.`);
    setIsAddingChapter(false);
    setEditingChapterId(null);
    setChapterFormData({ ...emptyChapterForm });
    loadChapters();
  } catch (error) {
    console.error('Save chapter error:', error);
    showAppAlert(`Failed to save chapter: ${error.message}`);
  }
};

const handleDeleteChapter = async (chapterId, chapterName) => {
  if (!isSuperAdmin) return;
  const ok = await requestConfirm({
    title: `Delete chapter "${chapterName}"?`,
    confirmLabel: 'Delete chapter',
    variant: 'danger',
    message: (
      <>
        <p>This removes the <code>/chapters/{chapterId}</code> document.</p>
        <p>Existing user, pitch, and review docs that reference this chapter by name are left untouched — you&rsquo;ll need to reassign or archive them separately.</p>
      </>
    ),
  });
  if (!ok) return;
  try {
    await deleteDoc(doc(db, "chapters", chapterId));
    showAppAlert(`Chapter "${chapterName}" deleted.`);
    loadChapters();
  } catch (error) {
    console.error('Delete chapter error:', error);
    showAppAlert(`Delete failed: ${error.message}`);
  }
};

// Seeds /chapters with the four known chapters from the legacy hardcoded maps in
// functions/index.js. Idempotent: skips any chapter whose slug already exists.
const handleSeedLegacyChapters = async () => {
  if (!isSuperAdmin) return;
  if (!showAppConfirm("Seed /chapters with the 4 known chapters?\n\n• Creates any missing chapter.\n• For existing chapters, fills in only missing fields (tagline, foundedYear, pageSlug, etc.). Does not overwrite anything you've edited.\n\nSafe to re-run.")) {
    return;
  }
  const legacyChapters = [
    {
      slug: 'wny', pageSlug: 'wny', name: 'Western New York',
      tagline: 'Where it all started, serving Buffalo and the surrounding 8 counties.',
      foundedYear: 2023,
      foundedDate: Timestamp.fromDate(new Date('2023-10-01')),
      emailAlias: 'wny@goodneighbor.fund', slackChannel: 'C04V14N4W83', lpSlackChannel: 'C04K9G2L29L', order: 1,
      heroTitle: '$1,000 Micro-Grants for Buffalo Business Ideas',
      heroTagline: 'No pitch deck required. No equity taken. Just belief in your vision and potential.',
      servingTitle: 'Serving All of Western New York',
      servingText: "Good Neighbor Fund WNY supports entrepreneurs across 8 counties in Western New York. Whether you're in the heart of Buffalo or rural Wyoming County, we believe in your potential.",
      counties: ['Erie County', 'Niagara County', 'Cattaraugus', 'Chautauqua', 'Allegany County', 'Genesee County', 'Wyoming County', 'Orleans County'],
      poweredByText: "Good Neighbor Fund is a collective giving organization. Our funding comes from Limited Partners (LPs) — local founders, operators, and community members who pool their own capital each quarter to fund the boldest new ideas in WNY. No overhead. No bureaucracy. Just neighbors investing in neighbors.",
      showLPs: true, showGallery: true
    },
    {
      slug: 'denver', pageSlug: 'denver', name: 'Denver',
      tagline: 'Serving the greater Denver metropolitan area.',
      foundedYear: 2023,
      foundedDate: Timestamp.fromDate(new Date('2024-01-01')),
      emailAlias: 'denver@goodneighbor.fund', slackChannel: 'C04ULN7FPB9', lpSlackChannel: 'C04K9G4PVML', order: 2,
      heroTitle: '$1,000 Micro-Grants for Denver Business Ideas',
      heroTagline: 'No pitch deck required. No equity taken. Just belief in your vision and potential.',
      servingTitle: 'Serving the Mile High City',
      servingText: 'Good Neighbor Fund Denver supports entrepreneurs across the Denver metro area. From downtown to the suburbs, we believe in your Mile High dreams.',
      counties: ['Denver County', 'Jefferson County', 'Arapahoe County', 'Adams County', 'Douglas County', 'Boulder County'],
      poweredByText: "Good Neighbor Fund is a collective giving organization. Our funding comes from Limited Partners (LPs) — local founders, operators, and community members who pool their own capital each quarter to fund the boldest new ideas in Denver. No overhead. No bureaucracy. Just neighbors investing in neighbors.",
      showLPs: true, showGallery: true
    },
    {
      slug: 'upstate', pageSlug: 'central', name: 'Central New York',
      tagline: 'Bringing belief capital to founders across Central New York — Syracuse, Ithaca, Binghamton, Utica and beyond.',
      foundedYear: 2026,
      foundedDate: Timestamp.fromDate(new Date('2026-05-26')),
      emailAlias: 'cny@goodneighbor.fund', slackChannel: 'C0AUSSA9DGW', lpSlackChannel: 'C0AUUTAB2BC', order: 3,
      heroTitle: '$1,000 Micro-Grants for Central NY Business Ideas',
      heroTagline: 'No pitch deck required. No equity taken. Just belief in your vision and potential.',
      servingTitle: 'Serving Central New York',
      servingText: "Good Neighbor Fund Central NY supports entrepreneurs across Central New York — from Syracuse to Ithaca, Binghamton to Utica. Wherever you're building, we believe in your potential.",
      counties: ['Onondaga County', 'Tompkins County', 'Broome County', 'Oneida County', 'Madison County', 'Cortland County', 'Tioga County', 'Chemung County'],
      poweredByText: "Good Neighbor Fund is a collective giving organization. Our funding comes from Limited Partners (LPs) — local founders, operators, and community members who pool their own capital each quarter to fund the boldest new ideas in Central NY. No overhead. No bureaucracy. Just neighbors investing in neighbors.",
      showLPs: false, showGallery: false
    },
    {
      slug: 'capital-region', pageSlug: 'capital-region', name: 'Capital Region',
      tagline: "Supporting bold ideas across New York's Capital Region — Albany, Schenectady, Troy and the surrounding area.",
      foundedYear: 2026,
      foundedDate: Timestamp.fromDate(new Date('2026-05-26')),
      emailAlias: 'capitalregion@goodneighbor.fund', slackChannel: 'C0B096U4KK2', lpSlackChannel: 'C0AV7QJS1TK', order: 4,
      heroTitle: '$1,000 Micro-Grants for Capital Region Business Ideas',
      heroTagline: 'No pitch deck required. No equity taken. Just belief in your vision and potential.',
      servingTitle: "Serving New York's Capital Region",
      servingText: "Good Neighbor Fund Capital Region supports entrepreneurs across Albany, Schenectady, Troy, Saratoga Springs and the surrounding counties. Wherever you're building, we believe in your potential.",
      counties: ['Albany County', 'Schenectady County', 'Rensselaer County', 'Saratoga County', 'Columbia County', 'Greene County', 'Washington County', 'Warren County'],
      poweredByText: "Good Neighbor Fund is a collective giving organization. Our funding comes from Limited Partners (LPs) — local founders, operators, and community members who pool their own capital each quarter to fund the boldest new ideas in the Capital Region. No overhead. No bureaucracy. Just neighbors investing in neighbors.",
      showLPs: false, showGallery: false
    },
  ];
  try {
    let created = 0, toppedUp = 0, unchanged = 0;
    for (const c of legacyChapters) {
      const existing = chapters.find(x => x.id === c.slug);
      if (!existing) {
        await setDoc(doc(db, "chapters", c.slug), {
          ...c,
          active: true,
          createdAt: Timestamp.now(),
          createdBy: user.uid,
          updatedAt: Timestamp.now()
        });
        created++;
        continue;
      }
      // Chapter exists — only fill in fields that are currently empty/missing.
      // Never overwrite a value the user has already set.
      const topUp = {};
      const isEmpty = (v) => v === undefined || v === null || v === '';
      for (const [k, v] of Object.entries(c)) {
        if (isEmpty(existing[k])) topUp[k] = v;
      }
      if (Object.keys(topUp).length === 0) {
        unchanged++;
      } else {
        topUp.updatedAt = Timestamp.now();
        await setDoc(doc(db, "chapters", existing.id), topUp, { merge: true });
        toppedUp++;
      }
    }
    showAppAlert(`Seed complete.\n• Created: ${created}\n• Topped up (added missing fields): ${toppedUp}\n• Already complete (no changes): ${unchanged}`);
    loadChapters();
  } catch (error) {
    console.error('Seed chapters error:', error);
    showAppAlert(`Seed failed: ${error.message}`);
  }
};

            // Chapter directors see only their own chapter; superAdmins see the full list.
            const visibleChapters = isSuperAdmin
              ? chapters
              : chapters.filter(c => c.name === userChapter);
            const chapterColSpan = isSuperAdmin ? 8 : 3;
            const renderChapterForm = () => {
                // Minimal local styles — grid layout only. Label / input typography
                // comes from `.mb-form-shell` via theme-tokens.css, which auto-themes
                // every input/textarea/select/label inside the form container.
                const fieldWrapStyle = { display: 'flex', flexDirection: 'column' };
                // auto-fit + min() so the same tracks read 2-up / 3-up on the
                // desktop form width but stack on phone widths.
                const gridTwoCol = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 240px), 1fr))', columnGap: '36px', rowGap: '24px', marginBottom: '24px' };
                const gridThreeCol = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 190px), 1fr))', columnGap: '32px', rowGap: '24px', marginBottom: '24px' };
                const tabIntroStyle = {
                  margin: '0 0 16px 0',
                  padding: '0 0 10px 0',
                  borderBottom: '1px solid var(--chrome-shade)',
                  boxShadow: '0 1px 0 var(--chrome-highlight)',
                };
                const tabEyebrowStyle = {
                  display: 'block',
                  fontFamily: 'var(--font-chrome)',
                  fontSize: 'var(--tc-label)',
                  fontWeight: 700,
                  color: 'var(--chrome-text-soft)',
                  textTransform: 'none',
                  letterSpacing: 'var(--track-label)',
                  marginBottom: '4px',
                };
                const tabHeadingStyle = {
                  margin: 0,
                  fontFamily: 'var(--font-display)',
                  fontSize: '18px',
                  fontWeight: 600,
                  letterSpacing: 'var(--track-head)',
                  color: 'var(--chrome-outline)',
                };
                const tabHelpStyle = {
                  display: 'block',
                  marginTop: '6px',
                  fontFamily: 'var(--font-content)',
                  fontSize: '12px',
                  color: 'var(--mb-ink-60)',
                  lineHeight: 1.5,
                };
                const helpStyle = {
                  display: 'block',
                  marginTop: '6px',
                  fontFamily: 'var(--font-content)',
                  fontSize: '12px',
                  color: 'var(--mb-ink-60)',
                  lineHeight: 1.5,
                };
                // Compact input overrides — tighter than the default `.mb-form-shell`
                // so dense admin forms don't feel as chunky as marketing-page fields.
                const labelStyle = { fontSize: '10px', letterSpacing: ' 0.01em' };
                const inputStyle = { width: '100%', padding: '7px 10px', fontSize: '13px', minHeight: 0, lineHeight: 1.3 };
                const monoInputStyle = { width: '100%', padding: '7px 10px', fontSize: '12px', minHeight: 0, lineHeight: 1.3, fontFamily: 'var(--font-numeral)' };
                const textareaStyle = { width: '100%', padding: '8px 10px', fontSize: '13px', lineHeight: 1.4, resize: 'vertical' };

                const countyList = Array.isArray(chapterFormData.counties)
                  ? chapterFormData.counties.map(s => String(s).trim()).filter(Boolean)
                  : [];
                const galleryList = Array.isArray(chapterFormData.galleryPhotos) ? chapterFormData.galleryPhotos : [];
                const heroBusy = !!uploadingPhotoFor[`hero:${editingChapterId}`];
                const galleryBusy = !!uploadingPhotoFor[`gallery:${editingChapterId}`];

                // Tab strip config. Chapter directors don't see Identity. The add-chapter
                // flow is Identity-only — Content/Photos/Visibility need a saved doc first.
                const tabs = [];
                if (isSuperAdmin) tabs.push({ key: 'identity', label: 'Identity' });
                if (!isAddingChapter) {
                  tabs.push({ key: 'content',    label: 'Content' });
                  tabs.push({ key: 'photos',     label: 'Photos' });
                  tabs.push({ key: 'visibility', label: 'Visibility' });
                }
                const activeTabKey = tabs.some(t => t.key === chapterFormTab) ? chapterFormTab : (tabs[0]?.key || 'content');

                return (
                <div
                  className="mb-form-shell"
                  style={{
                    width: '100%',
                    background: 'var(--chrome-face)',
                    border: '2px solid',
                    borderColor: 'var(--bevel-raised)',
                    boxShadow: 'var(--shadow-raised)',
                    marginBottom: 0,
                  }}
                >
                  {/* Sub-window titlebar — chrome gradient, like a nested window */}
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: '12px',
                    background: 'var(--titlebar-active)',
                    color: 'var(--titlebar-fg)',
                    textShadow: 'var(--titlebar-shadow)',
                    padding: '4px 10px',
                    minHeight: 'var(--titlebar-height)',
                    fontFamily: 'var(--font-chrome)',
                    fontSize: 'var(--tc-title)',
                    letterSpacing: 0,
                    textTransform: 'none',
                    fontWeight: 700,
                  }}>
                    <span>
                      {isAddingChapter ? 'New chapter' : `Editing — ${chapterFormData.name || '(unnamed)'}`}
                    </span>
                    {!isAddingChapter && (chapterFormData.pageSlug || chapterFormData.slug) && (
                      <a
                        href={`/${chapterFormData.pageSlug || chapterFormData.slug}`}
                        target="_blank"
                        rel="noreferrer"
                        style={{
                          fontSize: '11px',
                          color: 'var(--mb-butter)',
                          whiteSpace: 'nowrap',
                          textDecoration: 'underline',
                          textUnderlineOffset: '3px',
                        }}
                      >
                        View public page ↗
                      </a>
                    )}
                  </div>

                  {/* Tab strip — chrome folder tabs via the shared AdminTabStrip. */}
                  {tabs.length > 1 && (
                    <AdminTabStrip
                      tabs={tabs}
                      activeKey={activeTabKey}
                      onChange={setChapterFormTab}
                      ariaLabel="Chapter edit sections"
                    />
                  )}

                  <div style={{ padding: '16px 16px 12px', maxWidth: '720px' }}>
                    {/* ─── Identity & Admin ─── superAdmin only */}
                    {activeTabKey === 'identity' && isSuperAdmin && (
                      <section>
                        <div style={tabIntroStyle}>
                          <span style={tabEyebrowStyle}>Identity · Admin</span>
                          <h3 style={tabHeadingStyle}>Chapter identity &amp; routing</h3>
                          <small style={tabHelpStyle}>Display name, slug, contact email, Slack channel IDs. These drive notifications and access control.</small>
                        </div>

                        <div style={gridTwoCol}>
                          <div style={fieldWrapStyle}>
                            <label style={labelStyle}>Display name <span style={{ color: '#c00' }}>*</span></label>
                            <input
                              type="text"
                              value={chapterFormData.name}
                              onChange={(e) => setChapterFormData({ ...chapterFormData, name: e.target.value })}
                              placeholder="e.g. Western New York"
                              style={inputStyle}
                            />
                            <small style={helpStyle}>Must match the <code>chapter</code> value already on user/pitch/review docs.</small>
                          </div>
                          <div style={fieldWrapStyle}>
                            <label style={labelStyle}>
                              Slug (doc ID) {isAddingChapter && <span style={{ color: '#c00' }}>*</span>}
                            </label>
                            <input
                              type="text"
                              value={chapterFormData.slug}
                              onChange={(e) => setChapterFormData({ ...chapterFormData, slug: e.target.value })}
                              placeholder={`auto: ${slugifyChapter(chapterFormData.name) || 'from-name'}`}
                              disabled={!isAddingChapter}
                              style={{ ...monoInputStyle, background: !isAddingChapter ? '#eee' : 'var(--mb-chalk)' }}
                            />
                            <small style={helpStyle}>Firestore doc ID. Immutable after creation.</small>
                          </div>
                        </div>

                        <div style={gridTwoCol}>
                          <div style={fieldWrapStyle}>
                            <label style={labelStyle}>Page slug (public URL path)</label>
                            <input
                              type="text"
                              value={chapterFormData.pageSlug}
                              onChange={(e) => setChapterFormData({ ...chapterFormData, pageSlug: e.target.value })}
                              placeholder={`defaults to slug (${chapterFormData.slug || 'e.g. wny'})`}
                              style={monoInputStyle}
                            />
                            <small style={helpStyle}>e.g. <code>wny</code> → <code>goodneighbor.fund/wny</code>. Blank mirrors the slug.</small>
                          </div>
                          <div style={fieldWrapStyle}>
                            <label style={labelStyle}>Email alias</label>
                            <input
                              type="text"
                              value={chapterFormData.emailAlias}
                              onChange={(e) => setChapterFormData({ ...chapterFormData, emailAlias: e.target.value })}
                              placeholder="e.g. wny@goodneighbor.fund"
                              style={inputStyle}
                            />
                          </div>
                        </div>

                        <div style={{ ...fieldWrapStyle, marginBottom: '12px' }}>
                          <label style={labelStyle}>Tagline / short description</label>
                          <textarea
                            value={chapterFormData.tagline}
                            onChange={(e) => setChapterFormData({ ...chapterFormData, tagline: e.target.value })}
                            placeholder="e.g. Serving Buffalo and the surrounding 8 counties."
                            rows={2}
                            style={textareaStyle}
                          />
                          <small style={helpStyle}>Shown on the chapter list and the dynamic chapter page fallback.</small>
                        </div>

                        <div style={gridTwoCol}>
                          <div style={fieldWrapStyle}>
                            <label style={labelStyle}>Pitch Slack channel ID</label>
                            <input
                              type="text"
                              value={chapterFormData.slackChannel}
                              onChange={(e) => setChapterFormData({ ...chapterFormData, slackChannel: e.target.value })}
                              placeholder="e.g. C04V14N4W83"
                              style={monoInputStyle}
                            />
                          </div>
                          <div style={fieldWrapStyle}>
                            <label style={labelStyle}>LP application Slack channel ID</label>
                            <input
                              type="text"
                              value={chapterFormData.lpSlackChannel}
                              onChange={(e) => setChapterFormData({ ...chapterFormData, lpSlackChannel: e.target.value })}
                              placeholder="e.g. C04K9G2L29L"
                              style={monoInputStyle}
                            />
                          </div>
                        </div>

                        {/* Year, founding date, badge window & order. Founded date + window
                             drive the Founding LP badge: any LP joining within the window
                             of this date earns it automatically. Active lives on its own row
                             below as a dedicated toggle — a boolean doesn't belong next to
                             numeric fields. */}
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 150px), 1fr))', columnGap: '32px', rowGap: '24px', marginBottom: '24px', alignItems: 'start' }}>
                          <div style={fieldWrapStyle}>
                            <label style={labelStyle}>Founded year</label>
                            <input
                              type="number"
                              value={chapterFormData.foundedYear}
                              onChange={(e) => setChapterFormData({ ...chapterFormData, foundedYear: e.target.value })}
                              placeholder="e.g. 2026"
                              style={inputStyle}
                            />
                          </div>
                          <div style={fieldWrapStyle}>
                            <label style={labelStyle}>Founded date</label>
                            <input
                              type="date"
                              value={chapterFormData.foundedDate}
                              onChange={(e) => setChapterFormData({ ...chapterFormData, foundedDate: e.target.value })}
                              style={inputStyle}
                            />
                            <small style={helpStyle}>Drives Founding LP badge.</small>
                          </div>
                          <div style={fieldWrapStyle}>
                            <label style={labelStyle}>Founding window</label>
                            <select
                              value={normalizeFoundingWindow(chapterFormData.foundingWindowDays)}
                              onChange={(e) => setChapterFormData({ ...chapterFormData, foundingWindowDays: Number(e.target.value) })}
                              style={inputStyle}
                            >
                              {FOUNDING_WINDOW_OPTIONS.map((o) => (
                                <option key={o.days} value={o.days}>{o.label}</option>
                              ))}
                            </select>
                            <small style={helpStyle}>LPs joining within this span of the founded date earn Founding LP.</small>
                          </div>
                          <div style={fieldWrapStyle}>
                            <label style={labelStyle}>Order</label>
                            <input
                              type="number"
                              value={chapterFormData.order}
                              onChange={(e) => setChapterFormData({ ...chapterFormData, order: e.target.value })}
                              style={inputStyle}
                            />
                          </div>
                        </div>

                        {/* Active — dedicated checkbox-style toggle row. */}
                        <label style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 10,
                          padding: '8px 14px',
                          background: chapterFormData.active ? 'var(--mb-chalk)' : 'var(--mb-paper)',
                          border: '2px solid var(--mb-ink)',
                          cursor: 'pointer',
                          marginBottom: 0,
                          fontFamily: 'var(--font-content)',
                          fontSize: '13px',
                          textTransform: 'none',
                          letterSpacing: 0,
                          fontWeight: 600,
                          color: 'var(--mb-ink)',
                        }}>
                          <input
                            type="checkbox"
                            checked={chapterFormData.active}
                            onChange={(e) => setChapterFormData({ ...chapterFormData, active: e.target.checked })}
                            style={{ width: 16, height: 16, margin: 0, padding: 0, accentColor: 'var(--mb-magenta)' }}
                          />
                          <StatusIcon type={chapterFormData.active ? 'check' : 'cross'} size={14} />
                          <span>{chapterFormData.active ? 'Active' : 'Inactive'}</span>
                          <span style={{ color: 'var(--mb-ink-60)', fontWeight: 400, marginLeft: 4 }}>
                            {chapterFormData.active ? '— appears in chapter dropdowns' : '— hidden from dropdowns'}
                          </span>
                        </label>
                      </section>
                    )}

                    {/* ─── Landing-page content ─── editable by chapter_director + superAdmin */}
                    {activeTabKey === 'content' && !isAddingChapter && (
                      <section>
                        <div style={tabIntroStyle}>
                          <span style={tabEyebrowStyle}>Content · Public page</span>
                          <h3 style={tabHeadingStyle}>What visitors read on <code style={{ fontSize: '16px' }}>/{chapterFormData.pageSlug || chapterFormData.slug}</code></h3>
                          <small style={tabHelpStyle}>Hero copy, &ldquo;Serving&hellip;&rdquo; blurb, counties list, and the closing &ldquo;Powered by People&rdquo; paragraph. Blank fields fall back to defaults baked into the HTML.</small>
                        </div>

                        {/* Hero block — the H1 and its tagline are THE hero, so each gets its own row. */}
                        <div style={{ ...fieldWrapStyle, marginBottom: '24px' }}>
                          <label style={labelStyle}>Hero title (H1)</label>
                          <input
                            type="text"
                            value={chapterFormData.heroTitle}
                            onChange={(e) => setChapterFormData({ ...chapterFormData, heroTitle: e.target.value })}
                            placeholder='e.g. "$1,000 Micro-Grants for Buffalo Business Ideas"'
                            style={inputStyle}
                          />
                        </div>

                        <div style={{ ...fieldWrapStyle, marginBottom: '24px' }}>
                          <label style={labelStyle}>Hero tagline</label>
                          <textarea
                            value={chapterFormData.heroTagline}
                            onChange={(e) => setChapterFormData({ ...chapterFormData, heroTagline: e.target.value })}
                            placeholder='e.g. "No pitch deck required. No equity taken. Just belief in your vision and potential."'
                            rows={2}
                            style={{ ...textareaStyle, minHeight: '60px' }}
                          />
                          <small style={helpStyle}>The brand lede (<em>"We back brilliant ideas…"</em>) stays — this is the sentence that follows it.</small>
                        </div>

                        {/* Serving block — heading + description stacked vertically so heights never mismatch. */}
                        <div style={{ ...fieldWrapStyle, marginBottom: '24px' }}>
                          <label style={labelStyle}>"Serving…" heading</label>
                          <input
                            type="text"
                            value={chapterFormData.servingTitle}
                            onChange={(e) => setChapterFormData({ ...chapterFormData, servingTitle: e.target.value })}
                            placeholder='e.g. "Serving All of Western New York"'
                            style={inputStyle}
                          />
                        </div>
                        <div style={{ ...fieldWrapStyle, marginBottom: '24px' }}>
                          <label style={labelStyle}>"Serving…" description</label>
                          <textarea
                            value={chapterFormData.servingText}
                            onChange={(e) => setChapterFormData({ ...chapterFormData, servingText: e.target.value })}
                            placeholder="One paragraph describing the region."
                            rows={3}
                            style={{ ...textareaStyle, minHeight: '80px' }}
                          />
                        </div>

                        {/* Counties — narrower textarea, one county per line. */}
                        <div style={{ ...fieldWrapStyle, marginBottom: '24px', maxWidth: '520px' }}>
                          <label style={labelStyle}>
                            Counties
                            <span style={{ fontWeight: 'normal', color: 'var(--mb-ink-60)', marginLeft: '8px', textTransform: 'none', letterSpacing: 0, fontSize: 12 }}>
                              {countyList.length} {countyList.length === 1 ? 'county' : 'counties'}
                            </span>
                          </label>
                          <textarea
                            value={Array.isArray(chapterFormData.counties) ? chapterFormData.counties.join('\n') : chapterFormData.counties}
                            onChange={(e) => setChapterFormData({ ...chapterFormData, counties: e.target.value.split('\n') })}
                            placeholder={"Erie County\nNiagara County\nCattaraugus"}
                            rows={6}
                            style={{ ...textareaStyle, minHeight: '150px', fontFamily: 'var(--font-numeral)', fontSize: '12px' }}
                          />
                          <small style={helpStyle}>One county per line. Rendered as chips in the &ldquo;Serving&hellip;&rdquo; section on the public page.</small>
                        </div>

                        <div style={{ ...fieldWrapStyle, marginBottom: 0 }}>
                          <label style={labelStyle}>"Powered by People, Not Institutions" paragraph</label>
                          <textarea
                            value={chapterFormData.poweredByText}
                            onChange={(e) => setChapterFormData({ ...chapterFormData, poweredByText: e.target.value })}
                            placeholder="Describe how your chapter's LPs pool capital to fund local founders."
                            rows={4}
                            style={{ ...textareaStyle, minHeight: '110px' }}
                          />
                        </div>

                        <div style={{ ...fieldWrapStyle, marginBottom: '12px' }}>
                          <label style={labelStyle}>Default grant amount</label>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <span>$</span>
                            <input
                              type="number"
                              min="0"
                              step="1"
                              inputMode="numeric"
                              value={
                                chapterFormData.defaultGrantAmount === '' || chapterFormData.defaultGrantAmount == null
                                  ? ''
                                  : chapterFormData.defaultGrantAmount
                              }
                              onChange={(e) => setChapterFormData({
                                ...chapterFormData,
                                defaultGrantAmount: e.target.value === '' ? '' : Number(e.target.value)
                              })}
                              placeholder="1000"
                              style={{ ...monoInputStyle, width: 160 }}
                            />
                          </div>
                          <small style={helpStyle}>
                            Used by the Statistics tab as the dollar value of any winner pitch without an explicit award amount. Blank defers to the historical $1,000 baseline.
                          </small>
                        </div>
                      </section>
                    )}

                    {/* ─── Landing-page photos ─── editable by chapter_director + superAdmin */}
                    {activeTabKey === 'photos' && !isAddingChapter && (
                      <section>
                        <div style={tabIntroStyle}>
                          <span style={tabEyebrowStyle}>Photos · Hero + Gallery</span>
                          <h3 style={tabHeadingStyle}>Imagery for the public page</h3>
                          <small style={tabHelpStyle}>Uploads save immediately to <code>chapter-photos/{editingChapterId}/</code> &mdash; no &ldquo;Save&rdquo; click needed for photos.</small>
                        </div>

                          {/* Hero image */}
                          <div style={{ ...fieldWrapStyle, marginBottom: '16px' }}>
                            <label style={labelStyle}>Hero image</label>
                            <div style={{
                              display: 'flex',
                              gap: '14px',
                              alignItems: 'flex-start',
                              flexWrap: 'wrap',
                              marginTop: '4px',
                            }}>
                              <div style={{
                                width: '220px',
                                height: '140px',
                                background: 'var(--mb-chalk)',
                                border: '2px solid var(--mb-ink)',
                                boxShadow: 'var(--shadow-hard-sm)',
                                overflow: 'hidden',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0,
                              }}>
                                {chapterFormData.heroImage ? (
                                  <img
                                    src={chapterFormData.heroImage}
                                    alt="Hero preview"
                                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                  />
                                ) : (
                                  <span style={{ fontSize: '11px', color: 'var(--mb-ink-60)', fontFamily: 'var(--font-chrome)', letterSpacing: ' 0.01em', textTransform: 'none' }}>
                                    No image
                                  </span>
                                )}
                              </div>
                              <div style={{ flex: 1, minWidth: '220px', display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '10px' }}>
                                <label
                                  className="win95-btn win95-btn-primary"
                                  style={{ display: 'inline-block', textAlign: 'center', cursor: heroBusy ? 'wait' : 'pointer', opacity: heroBusy ? 0.6 : 1, padding: '5px 12px', fontSize: '12px' }}
                                >
                                  {heroBusy ? 'Uploading…' : chapterFormData.heroImage ? 'Replace hero image' : 'Upload hero image'}
                                  <input
                                    type="file"
                                    accept="image/*"
                                    style={{ display: 'none' }}
                                    disabled={heroBusy}
                                    onChange={(e) => {
                                      const file = e.target.files?.[0];
                                      if (file) handleChapterHeroUpload(file);
                                      e.target.value = '';
                                    }}
                                  />
                                </label>
                                <small style={helpStyle}>PNG/JPG, under 5 MB. Shown on the right side of the hero on <code>/{chapterFormData.pageSlug || chapterFormData.slug}</code>.</small>
                              </div>
                            </div>
                          </div>

                          {/* Hero caption */}
                          <div style={{ ...fieldWrapStyle, marginBottom: '18px' }}>
                            <label style={labelStyle}>Hero image caption</label>
                            <input
                              type="text"
                              value={chapterFormData.heroImageCaption}
                              onChange={(e) => setChapterFormData({ ...chapterFormData, heroImageCaption: e.target.value })}
                              placeholder={`e.g. "Fat Daddy's — Past Micro-Grant Awardee"`}
                              style={inputStyle}
                            />
                            <small style={helpStyle}>Small caption overlaid on the bottom of the hero photo. Leave blank to hide.</small>
                          </div>

                          {/* Gallery photos */}
                          <div style={fieldWrapStyle}>
                            <label style={labelStyle}>
                              Community gallery
                              <span style={{ fontWeight: 'normal', color: '#666', marginLeft: '8px' }}>
                                {galleryList.length} photo{galleryList.length === 1 ? '' : 's'}
                              </span>
                            </label>
                            <div style={{
                              display: 'grid',
                              gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
                              gap: '10px',
                              marginTop: '6px',
                              marginBottom: '10px',
                            }}>
                              {galleryList.map((photo, i) => (
                                <div key={photo.storagePath || photo.url || i} style={{
                                  position: 'relative',
                                  aspectRatio: '4 / 3',
                                  background: 'var(--mb-chalk)',
                                  border: '2px solid var(--mb-ink)',
                                  boxShadow: 'var(--shadow-hard-sm)',
                                  overflow: 'hidden',
                                }}>
                                  <img
                                    src={photo.url}
                                    alt={`Gallery ${i + 1}`}
                                    style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                                  />
                                  <button
                                    type="button"
                                    className="win95-btn win95-btn-danger"
                                    onClick={() => handleGalleryPhotoDelete(i)}
                                    style={{
                                      position: 'absolute',
                                      top: '4px',
                                      right: '4px',
                                      width: '26px',
                                      height: '26px',
                                      display: 'inline-flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      lineHeight: 1,
                                      padding: 0,
                                    }}
                                    aria-label={`Delete gallery photo ${i + 1}`}
                                  >
                                    ×
                                  </button>
                                </div>
                              ))}
                              <label
                                className="win95-btn"
                                style={{
                                  aspectRatio: '4 / 3',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  textAlign: 'center',
                                  cursor: galleryBusy ? 'wait' : 'pointer',
                                  opacity: galleryBusy ? 0.6 : 1,
                                  fontSize: '11px',
                                  lineHeight: 1.3,
                                  padding: '10px',
                                }}
                              >
                                {galleryBusy ? 'Uploading…' : '+ Add photo'}
                                <input
                                  type="file"
                                  accept="image/*"
                                  style={{ display: 'none' }}
                                  disabled={galleryBusy}
                                  onChange={(e) => {
                                    const file = e.target.files?.[0];
                                    if (file) handleGalleryPhotoUpload(file);
                                    e.target.value = '';
                                  }}
                                />
                              </label>
                            </div>
                          <small style={helpStyle}>Photos render in a grid under "Building &lt;Chapter&gt;'s Entrepreneurial Community." Toggle the whole grid on/off via "Show community gallery" below.</small>
                        </div>
                      </section>
                    )}

                    {/* ─── Section visibility ─── */}
                    {activeTabKey === 'visibility' && !isAddingChapter && (
                      <section>
                        <div style={tabIntroStyle}>
                          <span style={tabEyebrowStyle}>Visibility · Public page</span>
                          <h3 style={tabHeadingStyle}>Which sections appear on the landing page</h3>
                          <small style={tabHelpStyle}>Toggle whole blocks of the public <code>/{chapterFormData.pageSlug || chapterFormData.slug}</code> page on or off. Changes take effect immediately on save.</small>
                        </div>
                        <div style={{ ...gridTwoCol, marginBottom: 0 }}>
                          <div style={fieldWrapStyle}>
                            <label style={labelStyle}>
                              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                                <StatusIcon type={chapterFormData.showLPs ? 'check' : 'cross'} size={14} />
                                Show LP grid
                              </span>
                            </label>
                            <select
                              value={chapterFormData.showLPs ? 'true' : 'false'}
                              onChange={(e) => setChapterFormData({ ...chapterFormData, showLPs: e.target.value === 'true' })}
                              style={inputStyle}
                            >
                              <option value="true">Shown on public page</option>
                              <option value="false">Hidden</option>
                            </select>
                            <small style={helpStyle}>Toggles the "Chapter LPs" section on the landing page.</small>
                          </div>
                          <div style={fieldWrapStyle}>
                            <label style={labelStyle}>
                              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                                <StatusIcon type={chapterFormData.showImpact !== false ? 'check' : 'cross'} size={14} />
                                Show impact stats
                              </span>
                            </label>
                            <select
                              value={chapterFormData.showImpact !== false ? 'true' : 'false'}
                              onChange={(e) => setChapterFormData({ ...chapterFormData, showImpact: e.target.value === 'true' })}
                              style={inputStyle}
                            >
                              <option value="true">Shown on public page</option>
                              <option value="false">Hidden</option>
                            </select>
                            <small style={helpStyle}>Toggles the "Our Impact Since YYYY" section. Numbers are auto-calculated from this chapter's grant winners (count, women-owned %, BIPOC-owned %, total awarded).</small>
                          </div>
                          <div style={fieldWrapStyle}>
                            <label style={labelStyle}>
                              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                                <StatusIcon type={chapterFormData.showGallery ? 'check' : 'cross'} size={14} />
                                Show community gallery
                              </span>
                            </label>
                            <select
                              value={chapterFormData.showGallery ? 'true' : 'false'}
                              onChange={(e) => setChapterFormData({ ...chapterFormData, showGallery: e.target.value === 'true' })}
                              style={inputStyle}
                            >
                              <option value="true">Shown on public page</option>
                              <option value="false">Hidden</option>
                            </select>
                            <small style={helpStyle}>Toggles the photo grid at the bottom of the page.</small>
                          </div>
                          <div style={fieldWrapStyle}>
                            <label style={labelStyle}>
                              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                                <StatusIcon type={chapterFormData.showAwardees === true ? 'check' : 'cross'} size={14} />
                                Show grant awardees
                              </span>
                            </label>
                            <select
                              value={chapterFormData.showAwardees === true ? 'true' : 'false'}
                              onChange={(e) => setChapterFormData({ ...chapterFormData, showAwardees: e.target.value === 'true' })}
                              style={inputStyle}
                            >
                              <option value="false">Hidden</option>
                              <option value="true">Shown on public page</option>
                            </select>
                            <small style={helpStyle}>Adds a searchable, sortable grid of this chapter's past grant awardees to the landing page. Off by default.</small>
                          </div>
                        </div>
                      </section>
                    )}
                  </div>

                  {/* Save bar — chrome action bar footer inside the card */}
                  <div style={{
                    display: 'flex',
                    gap: 10,
                    justifyContent: 'flex-end',
                    padding: '8px 10px',
                    background: 'var(--chrome-face)',
                    borderTop: '2px solid var(--chrome-highlight)',
                  }}>
                    <RetroButton
                      onClick={() => {
                        setIsAddingChapter(false);
                        setEditingChapterId(null);
                        setChapterFormTab('content');
                        setChapterFormData({ ...emptyChapterForm });
                      }}
                    >
                      Cancel
                    </RetroButton>
                    <RetroButton onClick={handleSaveChapter} variant="primary">
                      Save chapter
                    </RetroButton>
                  </div>
                </div>
                );
              };
            return (
            <>
              <div className="win95-pane-header">
                <div>
                  <div className="win95-pane-header__context">
                    {isSuperAdmin ? "All chapters" : (userChapter || "Your chapter")} · public page settings
                  </div>
                  <h2 className="win95-pane-header__title">Chapter</h2>
                </div>
                {isSuperAdmin && (
                  <div className="win95-pane-header__stats">
                    <div className="win95-statpanel">
                      <div className="win95-statpanel__label">Chapters</div>
                      <div className="win95-statpanel__figure" style={{ color: "var(--mb-magenta-deep)" }}>
                        {visibleChapters.length}
                      </div>
                    </div>
                    <div className="win95-statpanel">
                      <div className="win95-statpanel__label">Active</div>
                      <div className="win95-statpanel__figure" style={{ color: "var(--mb-aqua-deep)" }}>
                        {visibleChapters.filter(c => c.active !== false).length}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div className="admin-pane-body">
              {isSuperAdmin && (() => {
                const legacySlugs = ['wny', 'denver', 'upstate', 'capital-region'];
                const missingSlugs = legacySlugs.filter(s => !chapters.some(c => c.id === s));
                if (missingSlugs.length === 0) return null;
                const isFreshSetup = chapters.length === 0;
                return (
                  <div className="admin-tool-card" style={{ marginBottom: 20 }}>
                    <div className="admin-tool-card__eyebrow">Setup</div>
                    <h5 className="admin-tool-card__title">
                      {isFreshSetup ? 'No chapters yet' : `Missing ${missingSlugs.length} chapter${missingSlugs.length === 1 ? '' : 's'}`}
                    </h5>
                    <div className="admin-tool-card__body">
                      {isFreshSetup ? (
                        <>Seed the collection with the 4 legacy chapters using the Slack channel and email values currently in <code>functions/index.js</code>.</>
                      ) : (
                        <>
                          Missing: <code>{missingSlugs.join(', ')}</code>. Seeding creates any missing chapters and fills in only empty fields on existing ones — it will never overwrite values you&rsquo;ve already edited.
                        </>
                      )}
                    </div>
                    <div className="admin-tool-card__action">
                      <RetroButton onClick={handleSeedLegacyChapters} variant="primary">
                        {isFreshSetup ? 'Seed legacy chapters' : 'Seed missing chapters'}
                      </RetroButton>
                    </div>
                  </div>
                );
              })()}

              {visibleChapters.length > 0 && (
                <div className="retro-table-wrap" style={{ marginBottom: 20 }}>
                  <table className="retro-table">
                    <thead>
                      <tr>
                        {isSuperAdmin && <th style={{ width: '40px' }}>#</th>}
                        <th>Chapter</th>
                        {isSuperAdmin && <th>Public page</th>}
                        {isSuperAdmin && <th>Contact</th>}
                        {isSuperAdmin && <th>Director(s)</th>}
                        <th style={{ textAlign: 'center', width: '130px' }}>Sections</th>
                        {isSuperAdmin && <th style={{ textAlign: 'center', width: '80px' }}>Status</th>}
                        <th style={{ width: '140px' }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {visibleChapters.map(c => {
                        // Directors are DERIVED, not stored. Any user with role=chapter_director
                        // OR chapterRole=chapter_director (the presentation-only tag for
                        // superAdmins who also serve as directors) and chapter matching this
                        // chapter's display name is a director.
                        const directors = users.filter(u =>
                          u.chapter === c.name &&
                          (u.role === 'chapter_director' || u.chapterRole === 'chapter_director')
                        );
                        const pageSlug = c.pageSlug || c.id;
                        const contactLines = [c.emailAlias, c.slackChannel && `pitch: ${c.slackChannel}`, c.lpSlackChannel && `lp: ${c.lpSlackChannel}`].filter(Boolean);
                        const isEditingThis = editingChapterId === c.id && !isAddingChapter;
                        return (
                          <React.Fragment key={c.id}>
                          <tr className={`${c.active === false ? 'is-inactive ' : ''}${isEditingThis ? 'is-editing' : ''}`}>
                            {isSuperAdmin && <td style={{ color: 'var(--mb-ink-60)', fontFamily: 'var(--font-numeral)' }}>{c.order ?? '—'}</td>}
                            <td>
                              <div style={{ fontWeight: 700 }}>{c.name || <span style={{ color: 'var(--mb-magenta-deep)' }}>(no name)</span>}</div>
                              {isSuperAdmin && <div style={{ fontFamily: 'var(--font-numeral)', fontSize: '11px', color: 'var(--mb-ink-60)', marginTop: 2 }}>{c.id}</div>}
                            </td>
                            {isSuperAdmin && (
                              <td>
                                <a href={`/${pageSlug}`} target="_blank" rel="noreferrer" style={{ fontFamily: 'var(--font-numeral)', fontSize: 12 }}>
                                  /{pageSlug} ↗
                                </a>
                              </td>
                            )}
                            {isSuperAdmin && (
                              <td style={{ fontSize: 12, lineHeight: 1.4 }}>
                                {contactLines.length === 0
                                  ? <span style={{ color: 'var(--mb-ink-60)' }}>—</span>
                                  : contactLines.map((line, i) => <div key={i} style={{ fontFamily: i === 0 ? 'var(--font-content)' : 'var(--font-numeral)' }}>{line}</div>)}
                              </td>
                            )}
                            {isSuperAdmin && (
                              <td style={{ fontSize: 13 }}>
                                {directors.length === 0 ? (
                                  <span style={{ color: 'var(--mb-ink-60)' }} title="Assign a user role 'Chapter Director' with this chapter in the Users tab.">none</span>
                                ) : (
                                  directors.map(d => d.name || d.email || d.uid).join(', ')
                                )}
                              </td>
                            )}
                            <td style={{ textAlign: 'center', fontSize: 12, whiteSpace: 'nowrap' }}>
                              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, marginRight: 10 }}>
                                LPs <StatusIcon type={c.showLPs === false ? 'cross' : 'check'} size={14} title={c.showLPs === false ? 'LP grid hidden' : 'LP grid shown'} />
                              </span>
                              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, marginRight: 10 }}>
                                Impact <StatusIcon type={c.showImpact === false ? 'cross' : 'check'} size={14} title={c.showImpact === false ? 'Impact stats hidden' : 'Impact stats shown'} />
                              </span>
                              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, marginRight: 10 }}>
                                Gallery <StatusIcon type={c.showGallery === false ? 'cross' : 'check'} size={14} title={c.showGallery === false ? 'Gallery hidden' : 'Gallery shown'} />
                              </span>
                              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                                Awardees <StatusIcon type={c.showAwardees === true ? 'check' : 'cross'} size={14} title={c.showAwardees === true ? 'Awardees shown' : 'Awardees hidden'} />
                              </span>
                            </td>
                            {isSuperAdmin && (
                              <td style={{ textAlign: 'center' }}>
                                <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                                  <StatusIcon type={c.active === false ? 'cross' : 'check'} size={16} title={c.active === false ? 'Inactive' : 'Active'} />
                                </span>
                              </td>
                            )}
                            <td className="actions">
                              <div style={{ display: 'inline-flex', gap: 6, alignItems: 'stretch' }}>
                              <RetroButton
                                size="sm"
                                style={{ minHeight: 28, minWidth: 56, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1 }}
                                onClick={() => {
                                  setEditingChapterId(c.id);
                                  setIsAddingChapter(false);
                                  setChapterFormTab('identity');
                                  setChapterFormData({
                                    slug: c.id,
                                    name: c.name || '',
                                    pageSlug: c.pageSlug || '',
                                    tagline: c.tagline || '',
                                    foundedYear: typeof c.foundedYear === 'number' ? c.foundedYear : '',
                                    foundedDate: c.foundedDate
                                      ? (c.foundedDate.toDate ? c.foundedDate.toDate() : new Date(c.foundedDate))
                                          .toISOString().split('T')[0]
                                      : '',
                                    foundingWindowDays: normalizeFoundingWindow(c.foundingWindowDays),
                                    emailAlias: c.emailAlias || '',
                                    slackChannel: c.slackChannel || '',
                                    lpSlackChannel: c.lpSlackChannel || '',
                                    active: c.active !== false,
                                    order: typeof c.order === 'number' ? c.order : 0,
                                    heroTitle: c.heroTitle || '',
                                    heroTagline: c.heroTagline || '',
                                    heroImage: c.heroImage || '',
                                    heroImageCaption: c.heroImageCaption || '',
                                    servingTitle: c.servingTitle || '',
                                    servingText: c.servingText || '',
                                    counties: Array.isArray(c.counties) ? c.counties : [],
                                    poweredByText: c.poweredByText || '',
                                    galleryPhotos: Array.isArray(c.galleryPhotos) ? c.galleryPhotos : [],
                                    showLPs: c.showLPs !== false,
                                    showGallery: c.showGallery !== false,
                                    showImpact: c.showImpact !== false,
                                    showAwardees: c.showAwardees === true,
                                    defaultGrantAmount: Number.isFinite(Number(c.defaultGrantAmount)) && Number(c.defaultGrantAmount) > 0
                                      ? Number(c.defaultGrantAmount)
                                      : '',
                                  });
                                }}
                              >
                                Edit
                              </RetroButton>
                              {isSuperAdmin && (
                                <RetroButton
                                  size="sm"
                                  variant="danger"
                                  onClick={() => handleDeleteChapter(c.id, c.name || c.id)}
                                  ariaLabel={`Delete chapter ${c.name || c.id}`}
                                  title={`Delete chapter ${c.name || c.id}`}
                                  style={{ minHeight: 28, minWidth: 36, padding: '0 10px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1 }}
                                >
                                  <StatusIcon type="trash" size={14} title="" />
                                </RetroButton>
                              )}
                              </div>
                            </td>
                          </tr>
                          {isEditingThis && (
                            <tr className="chapter-edit-row">
                              <td colSpan={chapterColSpan} style={{ padding: 0, background: 'var(--mb-chalk)' }}>
                                <div style={{ padding: '18px 20px' }}>{renderChapterForm()}</div>
                              </td>
                            </tr>
                          )}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}

              {isSuperAdmin && !isAddingChapter && !editingChapterId && !showNewChapterWizard && (
                <div style={{ display: 'flex', gap: '10px', marginBottom: '20px' }}>
                  <RetroButton
                    onClick={() => setShowNewChapterWizard(true)}
                    variant="primary"
                  >
                    New chapter wizard
                  </RetroButton>
                  <RetroButton
                    onClick={() => {
                      setIsAddingChapter(true);
                      setEditingChapterId(null);
                      setChapterFormTab('identity');
                      setChapterFormData({ ...emptyChapterForm, order: chapters.length + 1 });
                    }}
                  >
                    + Add chapter (manual)
                  </RetroButton>
                </div>
              )}

              {showNewChapterWizard && (
                <NewChapterWizard
                  user={user}
                  chapters={chapters}
                  onClose={() => setShowNewChapterWizard(false)}
                  onCreated={loadChapters}
                />
              )}

              {isAddingChapter && renderChapterForm()}
              </div>
            </>
            );
}
